import { useState, type FormEvent } from 'react';
import { SugerenciaEmail } from './onboarding/SugerenciaEmail';

/** Mismo criterio de formato que el validador del padrón (`validar-asociado`). */
const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;

/**
 * Pide el email del titular cuando la ficha no tiene ninguno, para poder
 * enviar la oferta MS2 o generar el link de pago. El email se GUARDA en la
 * ficha del titular (decisión 2026-07-28): sin él, el gateway de pagos rechaza
 * la intención con un HTTP 400 sin detalle y el operador queda sin saber por qué.
 *
 * Validaciones en cascada: formato acá → sugerencia anti-typo (`gmial.com`) →
 * el server chequea que el dominio reciba correo y que no lo use otro socio.
 * Los errores del server se muestran tal cual en `errorServidor`.
 */
export function EmailTitularModal({
  titular,
  accion,
  errorServidor,
  enviando,
  onCancelar,
  onConfirmar,
}: {
  titular: { nombre: string; documento: string };
  /** Para qué se pide el email — cambia el texto del botón. */
  accion: 'oferta' | 'pago';
  errorServidor?: string | null;
  enviando?: boolean;
  onCancelar: () => void;
  onConfirmar: (email: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [tocado, setTocado] = useState(false);

  const limpio = email.trim();
  const errorFormato = !limpio
    ? 'Ingresá un email.'
    : RE_EMAIL.test(limpio)
      ? null
      : 'El email no tiene un formato válido.';
  const mostrarError = tocado ? errorFormato : null;

  function submit(e: FormEvent) {
    e.preventDefault();
    setTocado(true);
    if (errorFormato) return;
    onConfirmar(limpio);
  }

  return (
    <div className="modalBackdrop" role="dialog" aria-modal="true" aria-labelledby="emailTitularTitulo">
      <form className="modalCard" onSubmit={submit} style={{ maxWidth: 460 }}>
        <h3 id="emailTitularTitulo" className="heading-5" style={{ margin: '0 0 6px' }}>
          Falta el email del titular
        </h3>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--color-element-mid)' }}>
          {accion === 'pago'
            ? 'Sin email no se puede generar el link de pago: el gateway lo necesita para emitir el cobro.'
            : 'Sin email no hay a dónde mandar la oferta.'}
        </p>

        <div
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            background: 'var(--color-surface-accent)',
            fontSize: 13,
            marginBottom: 14,
          }}
        >
          <strong>{titular.nombre}</strong>
          <br />
          DNI {titular.documento}
        </div>

        <label className="formField">
          <span>Email</span>
          <input
            className="input"
            type="email"
            autoFocus
            value={email}
            placeholder="nombre@dominio.com"
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTocado(true)}
            aria-invalid={Boolean(mostrarError)}
          />
        </label>
        <SugerenciaEmail email={limpio} onUsar={setEmail} />

        {mostrarError && (
          <p className="status status-error" style={{ display: 'block', marginTop: 10 }}>
            {mostrarError}
          </p>
        )}
        {errorServidor && !mostrarError && (
          <p className="status status-error" style={{ display: 'block', marginTop: 10 }}>
            {errorServidor}
          </p>
        )}

        <p style={{ margin: '14px 0 0', fontSize: 12, color: 'var(--color-element-mid)' }}>
          Se guarda en la ficha del titular, así no hay que volver a cargarlo.
        </p>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-ghost" onClick={onCancelar} disabled={enviando}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={enviando}>
            {enviando
              ? 'Guardando…'
              : accion === 'pago'
                ? 'Guardar y generar link'
                : 'Guardar y enviar'}
          </button>
        </div>
      </form>
    </div>
  );
}
